/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { PortableText } from '@portabletext/react';
import React, { useEffect, useRef, useState } from 'react';
import { FaCircleExclamation } from 'react-icons/fa6';
import { IoCheckmarkSharp } from 'react-icons/io5';
import submittedIcon from '../../images/formSubmittedIcon.svg';
import { getComponents } from '../../portableText';
import { RawPortableText } from '../../types/types';
import { GenericField, useForm, useFormField } from '../../utils/forms';
import { useTrackingData } from '../../utils/hooks';
import { clsx, replaceNewLinesWithBr, withDataLayer } from '../../utils/utils';
import * as styles from './Form.module.scss';
import InputField from './InputField';

export type FormField = {
  crmFieldId?: string;
  isFieldRequired?: boolean;
} & (
  | {
      fieldType: 'textSingleLine';
      title: string;
      withEmailValidation?: boolean;
    }
  | {
      fieldType: 'textMultiline';
      title: string;
      withEmailValidation?: never;
    }
  | {
      fieldType: 'singleCheckbox';
      title?: never;
      _rawText: RawPortableText;
      withEmailValidation?: never;
    }
);

export type FormFieldWithId = {
  id: string;
} & FormField;

export interface FormType {
  title?: string;
  submitButtonText?: string;
  thankYouScreen?: {
    title: string;
    text?: string;
  };
}

type FormProps = FormType & {
  title?: string;
  text?: React.ReactNode;
  fields: Array<FormFieldWithId>;
  withBackgroundColor?: boolean;
  hideLabels?: boolean;
  withSubmitButtonInsideInput?: boolean;
  labelBeforeInput?: React.ReactNode;
  showThankYouScreenAsLabel?: boolean;
  inputClassName?: string;
  className?: string;
};

const Form = ({
  title,
  text,
  fields,
  submitButtonText,
  thankYouScreen,
  withBackgroundColor,
  hideLabels,
  withSubmitButtonInsideInput,
  labelBeforeInput,
  showThankYouScreenAsLabel,
  inputClassName,
  className,
}: FormProps): React.ReactElement => {
  const fieldsByName = Object.fromEntries(
    fields.map(field => [
      field.id,
      field.fieldType === 'singleCheckbox'
        ? useFormField<boolean>(false, [...(field.isFieldRequired ? (['required'] as const) : [])])
        : useFormField<string>('', [
            ...(field.isFieldRequired ? (['required'] as const) : []),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ...(field.withEmailValidation && field.fieldType === 'textSingleLine'
              ? (['email'] as const)
              : []),
          ]),
    ]),
  );

  const { getFieldProps, renderSubmitButton, renderFormMessage, submitState, onFieldUnfocus } =
    useForm({
      fieldsByName,
      onSubmit,
      translateFunction: key => {
        return {
          'form.required_field_error': 'This field is required',
          'form.invalid_email_error': 'Invalid email (e.g. email@example.com)',
          'form.network_error': 'Network failed to send your request.',
          'form.unknown_error': 'An unexpected error occured. Please try again later.',
        }[key];
      },
    });

  const trackingData = useTrackingData();

  async function onSubmit() {
    if (process.env.NODE_ENV === 'development') {
      // Code to test form states (should be commented)
      // await wait(2000); // Case loading
      // throw new Error('Got response with status code 400'); // Case unknown error
      // throw new Error('Failed to fetch'); // Case network error
      return true; // Case success
    }

    const husbpotResp = await fetch('/.netlify/functions/submit-to-hubspot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: fieldsByName.email.value,
        referrer_website: trackingData.websiteReferrer,
        utm_campaign: trackingData.utmCampaign,
        utm_content: trackingData.utmContent,
        utm_device: trackingData.utmDevice,
        utm_source: trackingData.utmSource,
        utm_target: trackingData.utmTarget,
        utm_medium: trackingData.utmMedium,
        utm_term: trackingData.utmTerm,
      }),
    });

    if (husbpotResp.status !== 200) {
      if (husbpotResp.status === 400) {
        const data = await husbpotResp.json();
        if (data.error === 'INVALID_EMAIL') {
          fieldsByName.email.setError('Invalid email format.');
          return false;
        } else {
          throw new Error('Got response with status code ' + husbpotResp.status);
        }
      } else {
        throw new Error('Got response with status code ' + husbpotResp.status);
      }
    }

    withDataLayer(dataLayer => {
      dataLayer.push({ event: 'form-submit', form: 'contact', urlPath: location.pathname });
    });
    return true;
  }

  const [minFirstScreenContainerHeight, setMinFirstScreenContainerHeight] = useState<number | null>(
    null,
  );
  const firstScreenContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Warning: this code doesn't allow for the container to shrink, only grow
    function updateMinFirstScreenContainerHeight() {
      if (firstScreenContainerRef.current) {
        setMinFirstScreenContainerHeight(
          firstScreenContainerRef.current.getBoundingClientRect().height,
        );
      }
    }
    updateMinFirstScreenContainerHeight();
    window.addEventListener('resize', updateMinFirstScreenContainerHeight);
  }, []);

  return (
    <div
      className={clsx(
        styles.formContainer,
        withBackgroundColor && styles.withBackgroundColor,
        className,
      )}
      id="insight-form"
    >
      {(submitState === 'ready' ||
        submitState === 'submitting' ||
        (!title && !text && !showThankYouScreenAsLabel)) && (
        <form name="contact" data-netlify="true">
          <div className={styles.visuallyHidden}>Form</div>
          <input type="hidden" name="form-name" value="contact" />
          <div
            className={clsx(styles.formScreen, (!!text || !!title) && styles.withTextBlock)}
            ref={firstScreenContainerRef}
          >
            {title && <h2 className={styles.title}>{title}</h2>}
            <div
              className={clsx(
                styles.infoBlock,
                withSubmitButtonInsideInput && styles.withSubmitButtonInsideInput,
              )}
            >
              {text && text}

              <div className={styles.inputsContainer}>
                {labelBeforeInput && labelBeforeInput}

                {fields.map((formField, i) => {
                  if (formField.fieldType === 'singleCheckbox') {
                    const field = fieldsByName[formField.id] as GenericField<boolean>;
                    return (
                      <div
                        className={clsx(styles.checkboxContainer, !!field.error && styles.error)}
                        key={i}
                      >
                        <div className={styles.checkboxOptionContainer}>
                          <label htmlFor={formField.id} className={styles.checkboxLabel}>
                            <div className={styles.singleCheckboxText}>
                              <PortableText
                                value={formField._rawText}
                                components={getComponents()}
                              />
                            </div>
                            <input
                              className={clsx(styles.checkbox)}
                              type="checkbox"
                              name={formField.crmFieldId || formField.id}
                              id={formField.id}
                              checked={!!field.value}
                              onChange={event => {
                                field.setValue(event.target.checked);
                                field.setError('');
                              }}
                              onBlur={() => {
                                onFieldUnfocus(field);
                              }}
                              tabIndex={0}
                              onKeyDown={event => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  if (event.target && 'click' in event.target) {
                                    // @ts-expect-error
                                    event.target.click();
                                  }
                                }
                              }}
                            />
                            <span className={styles.checkmark} />
                          </label>
                        </div>
                        <div
                          // eslint-disable-next-line no-extra-boolean-cast
                          className={!!field.error ? styles.helperText : ''}
                          style={
                            withSubmitButtonInsideInput
                              ? { display: !field.error ? 'none' : 'block' }
                              : { visibility: !field.error ? 'hidden' : 'visible' }
                          }
                        >
                          <FaCircleExclamation />
                          {field.error}
                        </div>
                      </div>
                    );
                  }

                  const field = fieldsByName[formField.id] as GenericField<string>;
                  let inputElement: React.ReactElement;
                  if (formField.fieldType === 'textSingleLine') {
                    inputElement = (
                      <InputField
                        className={clsx(styles.input, field.error && styles.error, inputClassName)}
                        type={formField.withEmailValidation ? 'email' : 'text'}
                        id={formField.id + '-input'}
                        name={formField.crmFieldId || formField.id}
                        helperTextClass={styles.helperText}
                        helperTextWithDisplayNone
                        {...getFieldProps(field)}
                      />
                    );
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  } else if (formField.fieldType === 'textMultiline') {
                    inputElement = (
                      <InputField
                        textarea
                        className={clsx(
                          styles.input,
                          styles.textInput,
                          field.error && styles.error,
                        )}
                        id={formField.id + '-input'}
                        name={formField.crmFieldId || formField.id}
                        helperTextClass={styles.helperText}
                        {...getFieldProps(field)}
                      />
                    );
                  } else {
                    //@ts-expect-error
                    throw new Error(`Unknown fieldType "${formField.fieldType}"`);
                  }

                  return (
                    <div
                      key={formField.id}
                      // eslint-disable-next-line no-extra-boolean-cast
                      className={clsx(styles.inputContainer, !!field.error ? styles.error : '')}
                    >
                      <label
                        htmlFor={formField.id + '-input'}
                        className={clsx(styles.label, hideLabels && styles.visuallyHidden)}
                      >
                        {formField.title}
                        {formField.isFieldRequired ? '*' : ''}
                      </label>
                      {inputElement}
                      {renderSubmitButton({
                        labels: {
                          ready: submitButtonText || 'Send',
                          submitting: 'Loading...',
                          submitted: 'Sent',
                        },
                        btnClasses: {
                          common: styles.submitButton,
                          ready: styles.formReady,
                          submitting: styles.formSubmitting,
                          submitted: styles.formSubmitted,
                        },
                      })}
                    </div>
                  );
                })}
              </div>
              {renderFormMessage({
                styles: {
                  formMessage: styles.formMessage,
                  formMessageSuccess: styles.formMessageSuccess,
                  formMessageError: styles.formMessageError,
                },
              })}
            </div>
          </div>
        </form>
      )}
      {submitState === 'submitted' && thankYouScreen && (
        <div
          className={clsx(
            styles.thankYouScreenContainer,
            styles.withTextBlock,
            showThankYouScreenAsLabel && styles.showThankYouScreenAsLabel,
          )}
        >
          {showThankYouScreenAsLabel ? (
            <div className={styles.thankYouIcon}>
              <IoCheckmarkSharp />
            </div>
          ) : (
            <img src={submittedIcon} alt="" loading="lazy" />
          )}
          <div className={styles.thankYouTextContainer}>
            <h2 className={styles.thankYouTitle}>{thankYouScreen.title}</h2>
            {thankYouScreen.text && !showThankYouScreenAsLabel && (
              <p className={styles.thankYouText}>{replaceNewLinesWithBr(thankYouScreen.text)}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
